<template>
  <div class="content">
    <div class="edit" style="border: 1px solid #ccc;">
      <Toolbar 
        :editor="editorRef" 
        :defaultConfig="toolbarConfig" 
        :mode="mode" 
        style="border-bottom: 1px solid #ccc" 
      />
      <Editor 
        :defaultConfig="editorConfig" 
        :mode="mode" 
        v-model="valueHtml" 
        :style="{'height':height+'px'}"
        @onCreated="handleCreated" 
        @onChange="handleChange" 
        @onDestroyed="handleDestroyed" 
        @onFocus="handleFocus"
        @onBlur="handleBlur" 
        @customAlert="customAlert" 
        @customPaste="customPaste" 
      />
    </div>
  </div>
</template>

<script>
import '@wangeditor/editor/dist/css/style.css';
import { onBeforeUnmount, ref, shallowRef, onMounted, onUnmounted } from 'vue';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import { ElMessage } from 'element-plus'; // 引入Element的提示组件
import { UPLOAD_BASE_URL } from '@/common/constant';
import emitter from '@/utils/eventbus';

export default {
  components: { Editor, Toolbar },
  props: { 
    height: { type: Number, default: 284 },
    // 可通过props自定义最大上传大小（KB），默认300KB
    maxUploadSize: { type: Number, default: 300 }
  },
  emits: ['onHtmlChanged', 'onTextChanged'],
  setup(props, { emit }) {
    // 编辑器实例，必须用 shallowRef
    const editorRef = shallowRef();
    // 内容 HTML
    const valueHtml = ref('');

    // 监听外部事件更新编辑器内容
    emitter.on('changeUpdateCardCardInfo', (info) => {
      valueHtml.value = info;
    });

    // 编辑器获取焦点方法
    const getFocus = () => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.focus();
    };

    // 监听外部事件触发焦点
    emitter.on('changeCurrentMdEditOnFocus', getFocus);

    // 工具栏配置：排除不需要的功能
    const toolbarConfig = {
      excludeKeys: [
        "codeBlock",
        "fullScreen",
        'blockquote',
        'fontFamily',
        'group-video'
      ]
    };

    // 编辑器核心配置：重点修改上传大小限制
    const editorConfig = { 
      MENU_CONF: {}, 
      placeholder: '请输入内容...' 
    };

    // -------------------------- 图片上传配置（添加300KB限制）--------------------------
    editorConfig.MENU_CONF['uploadImage'] = {
      server: `${UPLOAD_BASE_URL}/manager/upload`,
      fieldName: 'file',
      // 最大文件大小：将props的KB转为字节（1KB=1024B），原代码10M注释错误，修正为300KB
      maxFileSize: props.maxUploadSize * 1024, 
      timeout: 60 * 1000, // 60秒超时
      
      // 自定义插入图片（原有逻辑保留）
      customInsert(res, insertFn) {
        if (!res?.data?.fileName) { // 判空，避免接口返回异常报错
          ElMessage.error('图片上传失败，请重试');
          return;
        }
        const url = res.data.fileName;
        insertFn(url, '', '');
      },
      onBeforeUpload(file) {
        // file 选中的文件，格式如 { key: file }
        return file
        // 可以 return
        // 1. return file 或者 new 一个 file ，接下来将上传
        // 2. return false ，不上传这个 file
      },
      // 上传错误提示（优化提示信息）
      onError(file, err, res) {
        ElMessage.error(`图片"${file.name}"上传失败：请检查图片大小`);
        console.warn(`${file.name} 上传出错`, err, res);
      }
    };

    // -------------------------- 视频上传配置（添加300KB限制）--------------------------
    editorConfig.MENU_CONF['uploadVideo'] = {
      server: `${UPLOAD_BASE_URL}/manager/upload`,
      fieldName: 'file',
      // 最大文件大小：同样限制为300KB（根据需求可调整，此处与图片保持一致）
      maxFileSize: props.maxUploadSize * 1024, 
      timeout: 120 * 1000, // 120秒超时
      
      // 新增：上传前校验文件大小
      onBeforeUpload(file) {
        return file;
      },
      
      // 自定义插入视频（原有逻辑保留，增加异常处理）
      customInsert(res, insertFn) {
        if (!res?.data?.fileName) {
          ElMessage.error('视频上传失败，请重试');
          return;
        }
        const url = res.data.fileName;
        insertFn(url);
      },
      
      // 视频上传错误提示
      onError(file, err, res) {
        ElMessage.error(`视频"${file.name}"上传失败：${err?.message || '网络异常'}`);
        console.warn(`${file.name} 上传出错`, err, res);
      }
    };

    // -------------------------- 编辑器生命周期与事件 --------------------------
    onMounted(() => {
      // 初始化时无需额外操作，editor实例在handleCreated中赋值
    });

    // 组件销毁：清理编辑器和事件监听，避免内存泄漏
    onBeforeUnmount(() => {
      const editor = editorRef.value;
      if (editor) {
        editor.destroy(); // 销毁编辑器
      }
      emitter.off('changeUpdateCardCardInfo');
      emitter.off('changeCurrentMdEditOnFocus');
    });

    // 组件卸载时再次清理事件（双重保障）
    onUnmounted(() => {
      emitter.off('changeUpdateCardCardInfo');
      emitter.off('changeCurrentMdEditOnFocus');
    });

    // 编辑器创建完成：赋值实例 + 自定义Tab键行为
    const handleCreated = (editor) => {
      editorRef.value = editor;
      // Tab键插入4个空格（原有逻辑保留）
      editor.handleTab = () => {
        editor.insertText('    ');
      };
    };

    // 编辑器内容变化：触发外部事件传递文本和HTML
    const handleChange = (editor) => {
      emit('onTextChanged', editor.getText());
      emit('onHtmlChanged', editor.getHtml());
    };

    // 其他回调函数（保留原有逻辑）
    const handleDestroyed = (/* editor */) => {};
    const handleFocus = (/* editor */) => {};
    const handleBlur = (/* editor */) => {};
    const customAlert = (info, type) => {
      // 自定义提示：优先用Element的提示，兼容编辑器默认alert
      if (type === 'error') {
        ElMessage.error(info);
      } else {
        ElMessage.info(info);
      }
    };
    const customPaste = (editor, event, callback) => {
      // 保留原有粘贴逻辑：允许默认粘贴行为
      callback(true);
    };

    // 编辑器操作方法（保留原有逻辑）
    const insertText = () => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.insertText('hello world');
    };
    const printHtml = () => {
      const editor = editorRef.value;
      if (editor == null) return;
      console.log('编辑器HTML内容：', editor.getHtml());
    };
    const disable = () => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.disable();
    };

    return {
      editorRef,
      mode: 'default',
      valueHtml,
      toolbarConfig,
      editorConfig,
      handleCreated,
      handleChange,
      handleDestroyed,
      handleFocus,
      handleBlur,
      customAlert,
      customPaste,
      insertText,
      printHtml,
      disable
    };
  },
};
</script>

<style scoped lang="less">
.content {
  display: flex;
  justify-content: space-around;
  width: 340px;

  .edit {
    width: 100%;

    .w-e-textarea-video-container {
      width: 100%;
    }

    .w-e-textarea-video-container video {
      width: 96%;
    }

    // 深度选择器：修复图片/视频容器宽度问题
    &:deep(.w-e-textarea-video-container) {
      width: 100% !important;
    }
    &:deep(.w-e-image-container) {
      width: inherit !important;
    }
    // 优化上传错误提示样式（避免与编辑器冲突）
    &:deep(.w-e-tooltip) {
      z-index: 1000 !important; // 提升提示层级，确保可见
    }
  }

  .view {
    width: 420px;
    textarea {
      height: 100%;
    }
  }
}
</style>